import { useEffect, useRef, useState } from "react";
import { AboutSlideshow } from "./AboutSlideshow";
import "./AboutSlideshow.css";

/**
 * ProphetsAbout — "What is Prophets?" (`#about`).
 *
 * Centered editorial slab with subtle living-light variation. Four body-copy
 * words (prediction · strategy · revenue · community) receive a very
 * restrained micro-glitch: 1–2px horizontal displacement + faint cyan/violet
 * chromatic separation + tiny silver brightness pulse, lasting ~380ms, then
 * the word snaps back to its normal state. Only one word glitches at a time,
 * with a random 5–9s pause between activations. `prefers-reduced-motion` and
 * page-visibility are respected — the effect fully disables under either.
 */

const GLITCH_WORDS = ["prediction", "strategy", "revenue", "community"];

/** Small helper: wraps a target word so the parent can toggle its glitch. */
function GlitchWord({ word, active }) {
  return (
    <span
      data-testid={`glitch-${word}`}
      data-text={word}
      className={`prophets-about-glitch${active ? " is-glitching" : ""}`}
    >
      {word}
    </span>
  );
}

export function ProphetsAbout() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [activeWord, setActiveWord] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold: 0.15, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Micro-glitch scheduler.
  useEffect(() => {
    if (!inView) return undefined;
    const mq = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
    if (mq && mq.matches) return undefined;

    let onTimeout = null;
    let offTimeout = null;
    let lastIdx = -1;

    const scheduleNext = (initial = false) => {
      // 3–5s before first fire; 5–9s between subsequent activations.
      const delay = initial
        ? 3000 + Math.random() * 2000
        : 5000 + Math.random() * 4000;
      onTimeout = setTimeout(() => {
        if (typeof document !== "undefined" && document.hidden) {
          // Tab not visible — try again shortly without firing.
          scheduleNext();
          return;
        }
        // Pick an index different from the previous one so no word repeats back-to-back.
        let idx = Math.floor(Math.random() * GLITCH_WORDS.length);
        if (idx === lastIdx) idx = (idx + 1) % GLITCH_WORDS.length;
        lastIdx = idx;
        setActiveWord(GLITCH_WORDS[idx]);
        // ~300–500ms flash, then release.
        const holdMs = 320 + Math.random() * 180;
        offTimeout = setTimeout(() => {
          setActiveWord(null);
          scheduleNext();
        }, holdMs);
      }, delay);
    };

    scheduleNext(true);

    const onVis = () => {
      // If the user returns to the tab and nothing is queued, keep going.
      // Existing timeouts naturally handle the paused case (we no-op inside).
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (onTimeout) clearTimeout(onTimeout);
      if (offTimeout) clearTimeout(offTimeout);
      document.removeEventListener("visibilitychange", onVis);
      setActiveWord(null);
    };
  }, [inView]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className={`prophets-about ${inView ? "is-in-view" : ""} relative w-full px-5 md:px-10 py-10 md:py-14`}
    >
      <div className="relative z-10 mx-auto max-w-[1200px] w-full">
        <article className="prophets-about-panel prophets-about-panel--split prophets-about-panel--frameless">
          {/* LEFT — 4-image autoplay slideshow (bleu → green → red → orange,
              loops infinitely with a sci-fi dematerialization transition).
              Hidden on mobile via the existing `.prophets-about-media` rule. */}
          <div className="prophets-about-media" aria-hidden="true">
            <AboutSlideshow />
            <span className="prophets-about-media-fade prophets-about-media-fade--right" />
            <span className="prophets-about-media-fade prophets-about-media-fade--top" />
            <span className="prophets-about-media-fade prophets-about-media-fade--bottom" />
          </div>

          <div className="prophets-about-content">
            <h2 data-testid="about-title" className="p-nav prophets-about-title">
              Beyond the Collection
              {/* One-shot silver sweep across the title */}
              <span aria-hidden="true" className="prophets-about-title-sweep" />
            </h2>

            <div className="prophets-about-body">
              <p>
                Prophets is the founding collection behind an expanding ecosystem of
                community-driven games.
              </p>
              <p>
                We build experiences that combine{" "}
                <GlitchWord word="prediction" active={activeWord === "prediction"} />,{" "}
                <GlitchWord word="strategy" active={activeWord === "strategy"} />, competition, and
                collective decision-making around real-world events and original Web3 formats.
              </p>
              <p>
                Holders are the pioneers of the ecosystem. They gain access to future games,
                exclusive opportunities, special benefits, and a share of the{" "}
                <GlitchWord word="revenue" active={activeWord === "revenue"} /> generated
                across the Prophets gaming ecosystem.
              </p>
              <p>
                The <GlitchWord word="community" active={activeWord === "community"} /> will also play an active role in shaping what comes next,
                proposing new concepts, influencing game development, testing ideas, and
                helping decide how the ecosystem evolves.
              </p>
            </div>

            <ul
              data-testid="about-stats"
              className="prophets-about-stats"
              aria-label="Collection facts"
            >
              <li className="prophets-about-stat" data-testid="about-stat-supply">
                <span className="p-nav prophets-about-stat-value">1,111</span>
                <span className="p-mono prophets-about-stat-label">Total Supply</span>
              </li>
              <li className="prophets-about-stat" data-testid="about-stat-families">
                <span className="p-nav prophets-about-stat-value">10</span>
                <span className="p-mono prophets-about-stat-label">Prophets Families</span>
              </li>
              <li className="prophets-about-stat" data-testid="about-stat-chain">
                <span className="prophets-about-stat-value-row">
                  <span aria-hidden="true" className="prophets-solana-glyph inline-flex flex-col justify-center items-start gap-[3px] mr-2 shrink-0">
                    <span className="prophets-solana-bar" />
                    <span className="prophets-solana-bar" />
                    <span className="prophets-solana-bar" />
                  </span>
                  <span className="p-nav prophets-about-stat-value">SOLANA</span>
                </span>
                <span className="p-mono prophets-about-stat-label">Blockchain</span>
              </li>
            </ul>
          </div>
        </article>
      </div>
    </section>
  );
}
